import { useMemo } from "react";
import { useAgents } from "../../../agent-catalog";
import type { Agent, OrgCategory } from "../../../generated/core-api";
import { filterServiceAccountsForOrg } from "../../utils/filterServiceAccountsForOrg";
import { useServiceAccountsForAgents } from "./useOrgAgentServiceAccounts";

export type OrgInvocableAgentTarget = {
  agent: Agent;
  serviceAccounts: ReturnType<typeof filterServiceAccountsForOrg>;
};

/**
 * Agents and org-scoped service accounts the current user may use for invocation.
 * Uses the same list/filter pattern as the provisioner service-account section;
 * API ReBAC limits which service accounts are returned for non-provisioners.
 */
export function useOrgInvocableAgentTargets(
  orgId: string,
  orgCategory: OrgCategory,
) {
  const agentsQuery = useAgents();
  const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data]);
  const agentIds = useMemo(
    () => agents.map((agent) => agent.id),
    [agents],
  );
  const serviceAccountQueries = useServiceAccountsForAgents(
    agentIds,
    agentsQuery.isSuccess,
  );

  const targets = useMemo(() => {
    const result: OrgInvocableAgentTarget[] = [];
    serviceAccountQueries.forEach((query, index) => {
      const agent = agents[index];
      if (!agent || !query.data) {
        return;
      }
      const orgAccounts = filterServiceAccountsForOrg(
        query.data,
        orgId,
        orgCategory,
      );
      if (orgAccounts.length > 0) {
        result.push({ agent, serviceAccounts: orgAccounts });
      }
    });
    return result;
  }, [agents, serviceAccountQueries, orgId, orgCategory]);

  const isLoading =
    agentsQuery.isLoading ||
    serviceAccountQueries.some((query) => query.isLoading);
  const isError =
    agentsQuery.isError ||
    serviceAccountQueries.some((query) => query.isError);

  return {
    targets,
    isLoading,
    isError,
    agentsQuery,
    serviceAccountQueries,
  };
}
